//react
import React, { useState, useEffect, useMemo } from "react";
import Highlighter from "react-highlight-words";

//UI
import { Drawer, Row, Col, Table, Empty, message } from "antd";
import { CardElement } from "../../../../components/Common/UIElements";
import CorporateContactPropertyUsersTopBar from "../../../../components/Onboarding/OnboardingUsers/PropertyContactUsers/CorporateContactPropertyUsersTopBar";
import PropertyUserForm from "../../../../components/Onboarding/PropertyUserForm/PropertyUserForm";
import HomePageTable from "../../../../components/Common/UIElements/HomePageTable";
import LoadingComponent from "../../../../components/Common/LoadingComponent";

import {
  ColoredTextTag,
  TEXT_TAG_COLORS,
} from "../../../../components/Common/ColoredTextTag/ColoredTextTag";

//processing
import useQuery from "../../../../hooks/url.hooks";
import { tableStringSorter } from "../../../../utils/table";
import {
  useActivateOnboardingUser,
  useInviteOnboardingUser,
} from "../../../../hooks/Onboarding/onboarding-users.hooks";
import { useGetPropertiesByCustomer } from "../../../../hooks/Onboarding/onboarding-properties.hooks";
import useTableSearch from "../../../../hooks/table-search.hooks";
import {
  ONBOARDING_PROPERTY_CONTACT_STATUS,
  RECORD_OPERATIONS,
} from "../../../../utils/onboarding-helper";
import { ButtonLink } from "../../../../components/Common/Button";

// set table height according to window
let tableHeight = 500;
if (window.innerWidth > 700) {
  tableHeight = window.innerHeight - 375;
}

const CorpContactPropertyUsers = () => {
  let query = useQuery();
  const msg = query.get("msg");
  const propertyId = query.get("property");
  const customerId = query.get("customer");

  useEffect(() => {
    if (msg) message.success(msg);
  }, []);

  const [property, setProperty] = useState(propertyId ?? "");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [operation, setOperation] = useState("");
  const [operationData, setOperationData] = useState("");

  const openDrawer = () => {
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const addPropertyUser = () => {
    setOperation(RECORD_OPERATIONS.ADD);
    setOperationData({});
    openDrawer();
  };

  const { mutateAsync: activatePropertyContact } = useActivateOnboardingUser({
    onSuccess: () => {
      message.success("Activation request successfully sent");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const { mutateAsync: invitePropertyContact } = useInviteOnboardingUser({
    onSuccess: () => {
      message.success("Reactivation request successfully sent");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const triggerDeactivatePropertyContact = (id) => {
    console.log("Deactivating ::", id);
  };

  const triggerActivatePropertyContact = (id) => {
    activatePropertyContact({ id });
  };

  const triggerResendInvitation = (id) => {
    invitePropertyContact({ id });
  };

  //render component for hilightable search text
  const searchHilighter = (dataIndex, customRender) => ({
    render: (text) => {
      return searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={searchedWords}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : customRender ? (
        customRender(text)
      ) : (
        text
      );
    },
  });

  //table columns
  const columns = [
    {
      title: "Property Name",
      dataIndex: "property_name",
      key: "property_name",
      width: "200px",
      sorter: tableStringSorter("property_name"),
      fixed: "left",
      ...searchHilighter("property_name"),
    },
    {
      title: "Property Contact First Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "250px",
      sorter: tableStringSorter("first_name"),
      ...searchHilighter("first_name"),
    },
    {
      title: "Property Contact Last Name",
      dataIndex: "last_name",
      key: "last_name",
      width: "250px",
      sorter: tableStringSorter("last_name"),
      ...searchHilighter("last_name"),
    },
    {
      title: "Property Contact Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      width: "275px",
      sorter: tableStringSorter("phone_number"),
      ...searchHilighter("phone_number"),
    },
    {
      title: "Property Contact Email",
      dataIndex: "email",
      key: "email",
      width: "230px",
      sorter: tableStringSorter("email"),
      ...searchHilighter("email"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "100px",
      fixed: "right",
      sorter: tableStringSorter("status"),
      ...searchHilighter("status", (status) => {
        let textColor =
          status === ONBOARDING_PROPERTY_CONTACT_STATUS.ACTIVE
            ? TEXT_TAG_COLORS.SUCCESS
            : status === ONBOARDING_PROPERTY_CONTACT_STATUS.PENDING
            ? TEXT_TAG_COLORS.WARN
            : "";

        return <ColoredTextTag text={status} color={textColor} />;
      }),
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "_id",
      width: "200px",
      render: (_id, record) => {
        const status = record.status;
        return (
          <Row>
            <Col>
              {status === ONBOARDING_PROPERTY_CONTACT_STATUS.ACTIVE && (
                <ButtonLink
                  onClick={() => {
                    triggerDeactivatePropertyContact(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>
                    Deactivate
                  </span>
                </ButtonLink>
              )}
              {status === ONBOARDING_PROPERTY_CONTACT_STATUS.IN_DRAFT && (
                <ButtonLink
                  onClick={() => {
                    triggerActivatePropertyContact(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>Activate</span>
                </ButtonLink>
              )}
              {status === ONBOARDING_PROPERTY_CONTACT_STATUS.PENDING && (
                <ButtonLink
                  onClick={() => {
                    triggerResendInvitation(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>
                    Resend Invitation
                  </span>
                </ButtonLink>
              )}
              {(status === ONBOARDING_PROPERTY_CONTACT_STATUS.IN_DRAFT ||
                status !== ONBOARDING_PROPERTY_CONTACT_STATUS.PENDING) && (
                <ButtonLink
                  onClick={() => {
                    setOperation(RECORD_OPERATIONS.EDIT);
                    setOperationData(record);
                    openDrawer();
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>Edit</span>
                </ButtonLink>
              )}
            </Col>
          </Row>
        );
      },
      fixed: "right",
    },
  ];

  //columns that could be searched
  const searchableColumns = columns.map((col) => col.dataIndex);

  const {
    data: apiAllPropertyContactData,
    isFetched: isFetchedAllPropertContacts,
  } = useGetPropertiesByCustomer(customerId, {
    enabled: !!customerId,
  });
  // custom hook for external multi column table data searching
  const {
    filteredTableData,
    searchedColumns,
    searchedWords,
    updateSearchMeta,
    searchMeta,
  } = useTableSearch({
    searchableColumns,
    allData: apiAllPropertyContactData ?? [],
  });

  useEffect(() => {
    updateSearchMeta(property, "searchProperty", ["_id"]);
  }, [property, apiAllPropertyContactData]);

  const propertiesData = useMemo(() => {
    let properties;

    if (Object.keys(searchMeta).length > 0 && filteredTableData) {
      properties = [...filteredTableData];
    } else {
      properties = apiAllPropertyContactData;
    }

    return properties?.map((property) => {
      return {
        _id: property.property_contact_id?._id,
        property_name: property.property_name,
        first_name: property.property_contact_id?.first_name,
        last_name: property.property_contact_id?.last_name,
        email: property.property_contact_id?.email,
        phone_number: property.property_contact_id?.phone_number || "",
        status: property.property_contact_id?.status,
        property_id: property._id,
        customer_id: property.customer_id,
      };
    });
  }, [apiAllPropertyContactData, searchMeta, filteredTableData]);

  return (
    <CardElement title="System Discovery">
      <>
        <CorporateContactPropertyUsersTopBar
          updateSearchMeta={updateSearchMeta}
          setProperty={setProperty}
          property={property}
          customer={customerId}
          addPropertyUser={addPropertyUser}
        />
        {Array.isArray(propertiesData) && propertiesData.length === 0 ? (
          <Empty description="No Property contact found" />
        ) : (
          <HomePageTable>
            <Table
              className="onboarding-properties-table"
              columns={columns}
              dataSource={propertiesData}
              rowKey="_id"
              scroll={{ x: 1100, y: tableHeight }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
              locale={{
                emptyText: isFetchedAllPropertContacts ? (
                  <div className="spinner-table">
                    <LoadingComponent />
                  </div>
                ) : null,
              }}
            />
          </HomePageTable>
        )}
      </>
      <Drawer
        title={`${
          operation === RECORD_OPERATIONS.EDIT ? "Edit" : "Add"
        } Property Contact User`}
        placement="right"
        open={drawerOpen}
        onClose={closeDrawer}
      >
        <PropertyUserForm
          setProperty={setProperty}
          property={property}
          closeDrawer={closeDrawer}
          operation={operation}
          operationData={operationData}
          customer={customerId}
          successRedirect={`/onboarding/corp-contact/property/users?property=${propertyId}`}
        />
      </Drawer>
    </CardElement>
  );
};

export default CorpContactPropertyUsers;
